import { defineResource } from '@tnn/core'
import { z } from 'zod'
import { signingField } from '../signature'

/**
 * Billing note (ใบวางบิล): several of one customer's issued invoices handed over together for
 * collection, with the date they are to be paid — for companies that invoice job by job and
 * collect once a month. The customer's clerk signs that they received it (`receivedBy`). An
 * invoice is on one billing note at a time (`invoice.billingNoteId`). Submitting numbers it
 * (`BN6910-0001`); a payment can then settle its invoices in one go (`payment.billingNoteId`).
 */
export const billingNote = defineResource({
  app: 'accounting',
  name: 'billingNote',
  path: 'billing-notes',
  schema: z.object({
    customerId: z.string().min(1),
    billedOn: z.iso.date(),
    /** When the customer will pay (นัดชำระ / นัดรับเช็ค) */
    dueDate: z.iso.date().nullish(),
    invoiceIds: z.array(z.string()).min(1),
    /** Who at the customer received it, and when */
    receivedBy: z.string().trim().max(200).nullish(),
    receivedOn: z.iso.date().nullish(),
    notes: z.string().max(1000).nullish(),
  }),
  fields: {
    number: { readOnly: true, search: true, list: { width: 140, priority: 1 } },
    billedOn: { filter: true, list: { width: 130, priority: 1 }, form: { span: 3 } },
    customerId: {
      type: 'ref',
      ref: { resource: 'accounting.customer', where: { status: 'active', isCustomer: 'true' } },
      filter: true,
      search: true,
      list: { priority: 1 },
      // Warns of what the printed document will go without (PartnerField)
      form: { span: 6, custom: true },
    },
    dueDate: { filter: true, list: { width: 130, priority: 2 }, form: { span: 3 } },
    invoiceIds: {
      type: 'multiselect',
      ref: { resource: 'accounting.invoice' },
      list: { priority: 3, hidden: true },
      form: { span: 12, section: 'invoices', custom: true },
    },
    /** The invoices' totals when it was submitted */
    amount: { readOnly: true, type: 'money', list: { align: 'end', width: 140, priority: 1 } },
    receivedBy: { search: true, list: { priority: 3, hidden: true }, form: { span: 6, section: 'received' } },
    receivedOn: { list: { priority: 3, hidden: true }, form: { span: 6, section: 'received' } },
    notes: { type: 'textarea', list: false, form: { span: 12, section: 'other' } },
    /** The customer's line: signed for, a link waiting, or not yet (server-set) */
    signing: signingField,
  },
  title: 'number',
  columns: ['number', 'billedOn', 'customerId', 'dueDate', 'amount', 'signing', 'receivedBy', 'receivedOn', 'invoiceIds'],
  sections: ['main', 'invoices', 'received', 'other'],
  defaultSort: '-billedOn',
  actions: ['read', 'create', 'update', 'delete', 'export', 'print'],
  submittable: { cancelReason: 'required' },
  card: { title: 'customerId', subtitle: 'number', meta: ['billedOn', 'amount'], status: 'docstatus' },
})
